import { useState, useCallback } from 'react';
import useSWR, { preload } from 'swr';
import { toast } from 'sonner';
import {
  listIntegrationConnectionsApi,
  listIntegrationMessagesApi,
  getIntegrationMessageApi,
  sendIntegrationMessageApi,
  initiateIntegrationAuthApi,
  disconnectIntegrationConnectionApi,
  listIntegrationThreadsApi,
  getIntegrationThreadApi,
} from '@/api/integrations';
import { API_BASE_URL } from 'src/constants/api.constants';
import type {
  NylasConnection,
  NylasMessage,
  NylasThread,
  NylasSendMessageRequestContent,
  NylasListMessagesRequestContent,
  NylasListThreadsRequestContent,
  EmailParticipant,
} from '@equaliq/api-model';

// Module-level fetcher shared by useNylasConnections and preloadNylasConnections.
// Sharing ONE fetcher reference under the same ['nylas-connections'] key lets
// SWR dedupe the org-select preload against the hook's own mount fetch
// (004_state.txt:170-171: preload() deduplicates with in-flight requests) — the
// connections fetch is the cold-start hop that gates everything in
// useAriInboxThreads (connectionId derives from connections[0]).
const connectionsFetcher = async () => {
  const response = await listIntegrationConnectionsApi();
  return response.connections ?? [];
};

// Pre-fetch helper, mirroring the useDealOperations.ts:104-105 precedent.
// Fire-and-forget (.catch(() => undefined)) per the shouldRetryOnError:false
// config below — a user with no Nylas grant fails the preload silently and
// one-shot, no toast (see the onError comment for why the toast was removed).
// Called from OrgContext's org-select fan-out to warm the cache before /inbox.
export const preloadNylasConnections = () =>
  preload(['nylas-connections'], connectionsFetcher).catch(() => undefined);

// Thin, stateless thread pre-fetch helper — DELIBERATELY without the
// useNylasThreads hook's setState side-effects (setNextToken / setReauthRequired
// / setExtraPages do not exist at module scope). It writes the SAME SWR key
// ['nylas-threads', connectionId, false] the hook reads in its DEFAULT
// (deal-only, includeUnlinked=false) view, so it only warms that slot; the
// hook's own mount revalidates into the same slot and re-establishes the
// reauth_required signal. The third key element MUST match the hook's default
// — R1-a keyed the hook on includeUnlinked, and the preload only ever warms the
// deal-only default view (it fires before the user can toggle), so `false` is
// the correct slot. Consumed by Item 2's stream provider, which holds the
// connections it derives connectionId from. Not chained from OrgContext (that
// would require reading the SWR cache imperatively to find connections[0]).
export const preloadNylasThreads = (connectionId: string) =>
  preload(
    ['nylas-threads', connectionId, false],
    async () =>
      (await listIntegrationThreadsApi({ connectionId, limit: 50, includeUnlinked: false })).threads ?? []
  ).catch(() => undefined);

/**
 * Hook to manage Nylas connections
 */
export function useNylasConnections(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const swrKey = enabled ? ['nylas-connections'] : null;

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    connectionsFetcher,
    {
      // Toast removed: this hook is mounted on every page that touches
      // calendar OR inbox (dashboard widgets, EventSheet, InboxPage,
      // CalendarPage). With a toast in onError, a single API failure
      // fires N toasts (one per mount) AND repeats every SWR
      // revalidation (focus, reconnect, 60s interval). For a user who
      // hasn't connected their email at all, the API legitimately
      // returns empty/error and the toast spam is pure noise. Surface
      // the error via the returned `error` field — let the consuming
      // component (e.g. InboxPage's "Connect Email" prompt) decide
      // whether to show anything user-facing. Console.error is kept
      // for ops debugging.
      onError: (err) => console.error('[useNylasConnections] Error:', err),
      // No retries: a 401/500 here is "user has no Nylas grant" or
      // "backend down" — neither benefits from rapid retries that
      // amplify the toast/log spam. Manual mutate() still re-fetches.
      shouldRetryOnError: false,
      revalidateOnFocus: false,
    }
  );

  return {
    connections: data ?? [],
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load connections') : null,
    refetch: () => mutate(),
  };
}

/**
 * Hook to initiate OAuth flow for connecting a new email account
 */
export function useConnectEmail() {
  const [isConnecting, setIsConnecting] = useState(false);

  const connectEmail = useCallback(async (provider?: string) => {
    setIsConnecting(true);
    try {
      const response = await initiateIntegrationAuthApi({ provider });

      // Open OAuth popup
      const width = 500;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        response.authUrl,
        'Connect Email',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for OAuth completion
      return new Promise<boolean>((resolve) => {
        const handleMessage = (event: MessageEvent) => {
          // Validate origin: the Lambda callback runs under API_BASE_URL (API Gateway)
          let expectedOrigin: string | null = null;
          try { expectedOrigin = new URL(API_BASE_URL).origin; } catch {}
          if (!expectedOrigin || event.origin !== expectedOrigin) {
            return;
          }

          if (event.data?.type === 'oauth-success' && event.data?.provider === 'nylas') {
            window.removeEventListener('message', handleMessage);
            popup?.close();
            toast.success('Email account connected successfully');
            setIsConnecting(false);
            resolve(true);
          } else if (event.data?.type === 'oauth-error') {
            window.removeEventListener('message', handleMessage);
            popup?.close();
            toast.error('Failed to connect email account');
            setIsConnecting(false);
            resolve(false);
          }
        };

        window.addEventListener('message', handleMessage);

        // Handle popup closed manually
        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            setIsConnecting(false);
            resolve(false);
          }
        }, 500);
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initiate connection';
      toast.error(errorMessage);
      setIsConnecting(false);
      return false;
    }
  }, []);

  return { connectEmail, isConnecting };
}

/**
 * Hook to disconnect an email account
 */
export function useDisconnectEmail() {
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const disconnectEmail = useCallback(async (connectionId: string) => {
    setIsDisconnecting(true);
    try {
      await disconnectIntegrationConnectionApi({ connectionId });
      toast.success('Email account disconnected');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to disconnect';
      toast.error(errorMessage);
      return false;
    } finally {
      setIsDisconnecting(false);
    }
  }, []);

  return { disconnectEmail, isDisconnecting };
}

/**
 * Hook to manage messages for a specific connection
 */
export function useNylasMessages(connectionId: string | null) {
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [extraPages, setExtraPages] = useState<NylasMessage[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const swrKey = connectionId ? ['nylas-messages', connectionId] : null;

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      const response = await listIntegrationMessagesApi({ connectionId: connectionId!, limit: 50 });
      setNextCursor(response.nextCursor);
      setExtraPages([]);
      return response.data ?? [];
    },
    {
      // SWR's default errorRetryInterval=5s + revalidateOnFocus=true
      // turned this hook into an unintended pollster: when the backend
      // /integrations/nylas/messages/list endpoint 403s (route not yet
      // shipped — task #77 marked complete prematurely), SWR retries
      // every 5s and re-fires on every focus event. Net effect: the
      // inbox tab spams the gateway forever even though no data could
      // possibly arrive. Disable retries + focus-revalidation; the user
      // can refresh the page if they need a manual retry. Real-time
      // updates should arrive via SSE, not polling — see follow-up.
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (err) => {
        console.error('[useNylasMessages] Error:', err);
      },
    }
  );

  const loadMore = useCallback(async () => {
    if (!connectionId || !nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const response = await listIntegrationMessagesApi({ connectionId, limit: 50, cursor: nextCursor });
      setExtraPages(prev => [...prev, ...(response.data ?? [])]);
      setNextCursor(response.nextCursor);
    } catch {
      toast.error('Failed to load more messages');
    } finally {
      setIsLoadingMore(false);
    }
  }, [connectionId, nextCursor, isLoadingMore]);

  const filterMessages = useCallback(
    (filters: Partial<NylasListMessagesRequestContent>) => {
      mutate(async () => {
        const response = await listIntegrationMessagesApi({ connectionId: connectionId!, limit: 50, ...filters });
        setNextCursor(response.nextCursor);
        setExtraPages([]);
        return response.data ?? [];
      });
    },
    [connectionId, mutate]
  );

  const messages = [...(data ?? []), ...extraPages];

  return {
    messages,
    isLoading: isLoading || isLoadingMore,
    error: error ? (error instanceof Error ? error.message : 'Failed to load messages') : null,
    nextCursor,
    refetch: () => mutate(),
    loadMore,
    filterMessages,
  };
}

/**
 * Hook to get a specific message by ID
 */
export function useNylasMessage(connectionId: string | null, messageId: string | null) {
  const swrKey = connectionId && messageId ? ['nylas-message', connectionId, messageId] : null;

  const { data, error, isLoading } = useSWR(
    swrKey,
    async () => {
      const response = await getIntegrationMessageApi({ connectionId: connectionId!, messageId: messageId! });
      return response.data;
    },
    {
      onError: () => toast.error('Failed to load message details'),
    }
  );

  return {
    message: data ?? null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load message') : null,
  };
}

/**
 * Hook to send messages
 */
export function useSendMessage() {
  const [isSending, setIsSending] = useState(false);

  const sendMessage = useCallback(async (
    connectionId: string,
    to: EmailParticipant[],
    subject: string,
    body: string,
    options?: {
      cc?: EmailParticipant[];
      bcc?: EmailParticipant[];
      replyToMessageId?: string;
    }
  ) => {
    setIsSending(true);
    try {
      const request: NylasSendMessageRequestContent = {
        connectionId,
        to,
        subject,
        body,
        ...options,
      };
      const response = await sendIntegrationMessageApi(request);
      toast.success('Message sent successfully');
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsSending(false);
    }
  }, []);

  return { sendMessage, isSending };
}

/**
 * Hook to manage threads for a specific connection
 * Threads include AI-generated metadata (summary, priority, labels, keywords)
 */
export function useNylasThreads(
  connectionId: string | null,
  // R1-a escape hatch — DEFAULT OFF. When true ("Needs review / All mail"
  // view) the api-server LEFT-joins so unlinked threads survive carrying the
  // categorizer verdict. Part of the SWR key so toggling the view re-fetches
  // into a distinct cache slot rather than serving the deal-only list.
  includeUnlinked = false,
) {
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [extraPages, setExtraPages] = useState<NylasThread[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Tracks the structured reauth signal the api-server returns when the
  // Nylas grant's underlying provider OAuth token has been revoked at
  // the IdP. Surfaces to the InboxPage so it can render a Reconnect CTA
  // instead of the generic "No threads" empty state.
  const [reauthRequired, setReauthRequired] = useState<{ provider: string } | null>(null);
  // Keying on includeUnlinked keeps the two views in separate SWR slots — the
  // deal-only list stays warm when the user flips back. `false` is the default
  // slot, byte-for-byte the original ['nylas-threads', connectionId, false].
  const swrKey = connectionId ? ['nylas-threads', connectionId, includeUnlinked] : null;

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      try {
        const response = await listIntegrationThreadsApi({ connectionId: connectionId!, limit: 50, includeUnlinked });
        setNextToken(response.nextToken);
        setExtraPages([]);
        setReauthRequired(null);
        return response.threads ?? [];
      } catch (err: any) {
        // api-server returns 422 with { error: 'reauth_required', provider }
        // when the underlying provider OAuth token is dead. Surface that
        // signal as state — and return [] so SWR doesn't bubble an
        // exception that the api-client interceptor would mistake for a
        // session-401.
        const body = err?.response?.data;
        if (err?.response?.status === 422 && body?.error === 'reauth_required') {
          setReauthRequired({ provider: body.provider ?? 'google' });
          setNextToken(undefined);
          setExtraPages([]);
          return [];
        }
        throw err;
      }
    },
    {
      // Same SWR retry-spam fix as useNylasMessages above. Backend
      // endpoint /integrations/nylas/threads/list is not yet shipped;
      // until it is, SWR's default retry-on-error + revalidate-on-focus
      // would turn this into a 5s pollster forever. Real-time updates
      // should land via SSE post-connection, not polling.
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (err) => {
        console.error('[useNylasThreads] Error:', err);
      },
    }
  );

  const loadMore = useCallback(async () => {
    if (!connectionId || !nextToken || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const response = await listIntegrationThreadsApi({ connectionId, limit: 50, nextToken, includeUnlinked });
      setExtraPages(prev => [...prev, ...(response.threads ?? [])]);
      setNextToken(response.nextToken);
    } catch {
      toast.error('Failed to load more threads');
    } finally {
      setIsLoadingMore(false);
    }
  }, [connectionId, nextToken, isLoadingMore, includeUnlinked]);

  const filterThreads = useCallback(
    (filters: Partial<NylasListThreadsRequestContent>) => {
      mutate(async () => {
        const response = await listIntegrationThreadsApi({ connectionId: connectionId!, limit: 50, includeUnlinked, ...filters });
        setNextToken(response.nextToken);
        setExtraPages([]);
        return response.threads ?? [];
      });
    },
    [connectionId, mutate, includeUnlinked]
  );

  const threads = [...(data ?? []), ...extraPages];

  return {
    threads,
    // Combined flag — initial fetch OR append-in-progress.
    isLoading: isLoading || isLoadingMore,
    // Separate flag — only pagination. The Load-More button in
    // InboxPage needs to disable on append without suppressing the
    // initial list render.
    isLoadingMore,
    error: error ? (error instanceof Error ? error.message : 'Failed to load threads') : null,
    // Set when the api-server signaled reauth_required (Nylas grant's
    // underlying OAuth token revoked or expired at the provider IdP).
    // Inbox page renders the Reconnect CTA when this is non-null.
    reauthRequired,
    nextToken,
    refetch: () => mutate(),
    loadMore,
    filterThreads,
  };
}

/**
 * Hook to get a specific thread by ID
 */
export function useNylasThread(threadMetadataId: string | null) {
  const swrKey = threadMetadataId ? ['nylas-thread', threadMetadataId] : null;

  const { data, error, isLoading } = useSWR(
    swrKey,
    async () => {
      const response = await getIntegrationThreadApi({ threadMetadataId: threadMetadataId! });
      return response.thread;
    },
    {
      onError: () => toast.error('Failed to load thread details'),
    }
  );

  return {
    thread: data ?? null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load thread') : null,
  };
}